import { Fragment } from "react";
import { formatCost, formatMilliseconds } from "@/helpers/utils";
import { useAppState } from "@/modules/app/useAppContext";
import { TaskRunsSummaryResponse } from "@/pages/query/types";
import { Stack } from "@altimateai/lego";

/**
 * Aggregate stats for one task's run history, from GET /query/v2/tasks/runs_summary.
 *
 * The shared CommonSummary can't be reused here: tasks expose queue time,
 * failure counts, and `_ms`-suffixed fields that the stored-procedures /
 * streamlit summary shape doesn't carry, and they lack min/max runtime.
 */
export const TasksRunsSummary = ({
  summaryData,
  database,
  schema,
}: {
  summaryData?: TaskRunsSummaryResponse;
  /** Optional fallbacks from URL params — used when summaryData doesn't carry
   *  the names (kept for caller resilience; the API does include them). */
  database?: string;
  schema?: string;
}) => {
  const { currency } = useAppState();
  if (!summaryData) {
    return null;
  }

  const successRatio =
    summaryData.run_count > 0
      ? (summaryData.success_count / summaryData.run_count).toFixed(2)
      : "—";

  // Database/Schema are folded INTO the summary row instead of being a
  // separate caption under the page title — keeps the header compact and
  // makes every piece of metadata follow the same label:value pattern.
  const items = [
    // URL params are the user-driven identity → they win over the API echo.
    // (In practice they match; this just keeps the displayed path stable if
    // the backend ever returns a normalized casing variant.)
    { label: "Database", value: database || summaryData.database_name || "—" },
    { label: "Schema", value: schema || summaryData.schema_name || "—" },
    { label: "Total runs", value: summaryData.run_count.toLocaleString("en-US") },
    { label: "Success ratio", value: successRatio },
    { label: "Failures", value: summaryData.failure_count.toLocaleString("en-US") },
    { label: "Avg runtime", value: formatMilliseconds(summaryData.avg_execution_time_ms) },
    { label: "Avg queue time", value: formatMilliseconds(summaryData.avg_queued_time_ms) },
    { label: "Total cost", value: formatCost(summaryData.total_cost, currency) },
    { label: "Avg cost", value: formatCost(summaryData.avg_cost, currency) },
    { label: "Last run", value: summaryData.last_run_date ?? "—" },
  ];

  return (
    <Stack
      align={"center"}
      wrap
      justify={"between"}
      className="al-w-full"
      tag="dl"
      aria-label="Summary"
    >
      {items.map(item => (
        <Fragment key={item.label}>
          <dt className="al-text-light-gray">{item.label}:</dt>
          <dd aria-label={item.label} className="al-font-semibold al-m-0">
            {item.value}
          </dd>
        </Fragment>
      ))}
    </Stack>
  );
};
